import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link, Navigate } from '@sorare/routing';
import { Container } from '@sorare/core/src/atoms/layout/Container';
import { ANY_SPORT_SERIES_ONBOARDING_SELECT_TEAM_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { Background } from '#src/components/onboarding/series/Background/index.tsx';
import { ListSection } from '#src/components/onboarding/series/ListSection/index.tsx';
import { ListTile } from '#src/components/onboarding/series/ListTile/index.tsx';
import { SeriesOnboardingHeader } from '#src/components/onboarding/series/SeriesOnboardingHeader/index.tsx';

import type {
  SelectCompetitionQuery,
  SelectCompetitionQueryVariables,
} from './__generated__/page.graphql.ts';

const MainWrapper = styled(Container)`
  padding: var(--double-unit) 0;
`;

const SELECT_COMPETITION_QUERY = gql`
  query SelectCompetitionQuery($sport: Sport!) {
    currentUser {
      slug
      anyUserSportProfile(sport: $sport) {
        id
        onboardingCompetitions {
          slug
          displayName
          pictureUrl
        }
      }
    }
  }
` as TypedDocumentNode<SelectCompetitionQuery, SelectCompetitionQueryVariables>;

export const loader = queryLoader(SELECT_COMPETITION_QUERY, ({ sport }) => ({
  sport,
}));

const SelectCompetitionPage = () => {
  const { data } = useQueryLoader<SelectCompetitionQuery>();
  const { generateSportContextPath } = useSportContext();

  const { onboardingCompetitions = [] } =
    data.currentUser?.anyUserSportProfile || {};

  if (onboardingCompetitions.length === 1) {
    return (
      <Navigate
        replace
        to={generateSportContextPath(
          ANY_SPORT_SERIES_ONBOARDING_SELECT_TEAM_SLUG,
          {
            params: {
              slug: onboardingCompetitions[0].slug,
            },
          }
        )}
      />
    );
  }

  return (
    <Background>
      <MainWrapper>
        <div>
          <SeriesOnboardingHeader
            title={
              <FormattedMessage defaultMessage="What's your favorite league?" />
            }
          />
          <ListSection title={<FormattedMessage defaultMessage="Leagues" />}>
            {onboardingCompetitions.map(onboardingCompetition => {
              const { slug, displayName, pictureUrl } = onboardingCompetition;

              return (
                <Link
                  key={slug}
                  to={generateSportContextPath(
                    ANY_SPORT_SERIES_ONBOARDING_SELECT_TEAM_SLUG,
                    {
                      params: {
                        slug,
                      },
                    }
                  )}
                >
                  <ListTile pictureUrl={pictureUrl} name={displayName} />
                </Link>
              );
            })}
          </ListSection>
        </div>
      </MainWrapper>
    </Background>
  );
};

export default SelectCompetitionPage;
